import axios from 'axios'
import ResizerImport from 'react-image-file-resizer'
import { envConfig } from '@/shared/config/envConfig'
import { saveToLocalStorage } from '@/shared/utils/localStorageHelper'

// react-image-file-resizer is a CommonJS module. Under Vite/esbuild the default
// import can arrive double-wrapped ({ default: Resizer }), so `Resizer.imageFileResizer`
// is undefined. Unwrap defensively so uploads work under both Vite and Next.
const Resizer = ((ResizerImport as { default?: typeof ResizerImport }).default ??
    ResizerImport) as typeof ResizerImport

const MAXIMUM_IMAGE_SIZE = 4000000 // 4MB maximum image size

type ResizedImageResult = string | Blob | File | ProgressEvent<FileReader>

type CloudinaryUploadResponse = {
    secure_url?: string
    url?: string
    public_id?: string
    resource_type?: string
    [key: string]: unknown
}

const resizeFile = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        // imageFileResizer reports failure by throwing synchronously; without
        // this the promise would never settle and the caller would hang.
        try {
            Resizer.imageFileResizer(
                file,
                250, // Is the maxWidth of the resized new image.
                250, // Is the maxHeight of the resized new image.
                'WEBP', // Is the compressFormat of the resized new image.
                70,
                0,
                (uri: ResizedImageResult) => {
                    resolve(typeof uri === 'string' ? uri : '')
                },
                'base64'
            )
        } catch (error) {
            reject(error instanceof Error ? error : new Error('Image resize failed'))
        }
    })

const uploadToCloudinary = async (dataUrl: string): Promise<CloudinaryUploadResponse> => {
    const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${envConfig.cloudinaryCloudName}/image/upload` // Replace with your Cloudinary upload endpoint
    const formData = new FormData()
    formData.append('file', dataUrl)

    formData.append('upload_preset', envConfig.cloudinaryUploadPreset || '')
    formData.append('cloud_name', envConfig.cloudinaryCloudName || '')
    formData.append('folder', envConfig.cloudinaryFolderName || '')

    const response = await axios.post(cloudinaryUrl, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
    return response.data
}

const uploadFileToCloudinary = async (
    file: File,
    resourceType: 'image' | 'video' | 'raw' | 'auto' = 'auto'
): Promise<CloudinaryUploadResponse> => {
    const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${envConfig.cloudinaryCloudName}/${resourceType}/upload`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', envConfig.cloudinaryUploadPreset || '')
    formData.append('cloud_name', envConfig.cloudinaryCloudName || '')
    formData.append('folder', envConfig.cloudinaryFolderName || '')

    const response = await axios.post(cloudinaryUrl, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
    return response.data
}

type FormValue = {
    picture?: unknown
}

function firstPicture(value: unknown): File | undefined {
    if (Array.isArray(value) && value[0] instanceof File) return value[0]
    if (value instanceof FileList && value[0] instanceof File) return value[0]
    return undefined
}

const resizeAndUploadImage = async (
    formValue: FormValue,
    imageUrl?: unknown
): Promise<string> => {
    if (typeof imageUrl === 'string' && imageUrl.trim()) {
        return imageUrl
    }

    const imageBlob = firstPicture(formValue.picture)

    if (!imageBlob) {
        throw new Error('No image file found in formValue.picture')
    }

    const dataUrl = await resizeFile(imageBlob)
    const imageUploadUrl = await uploadToCloudinary(dataUrl)

    if (!imageUploadUrl.secure_url) {
        throw new Error('Image upload failed')
    }

    saveToLocalStorage('image-url', imageUploadUrl.secure_url)
    return imageUploadUrl.secure_url
}

export {
    resizeFile,
    uploadToCloudinary,
    uploadFileToCloudinary,
    resizeAndUploadImage,
    MAXIMUM_IMAGE_SIZE
}
